/**
 * PIN Lock Screen — Touch-Optimized Numeric Keypad
 *
 * 4-digit PIN entry with auto-submit on 4th digit.
 * Shows lockout countdown when account is locked.
 * Location badge with color from device info.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import teletimeLogoWhite from '../assets/logos/teletime-logo-white-400.png';

function PinLockScreen() {
  const { pinLogin, logout, deviceInfo, error, clearError, getLocationColor } = useAuth();

  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [shake, setShake] = useState(false);
  const [localError, setLocalError] = useState('');
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const lockoutTimerRef = useRef(null);

  const locationColor = deviceInfo?.locationColor || getLocationColor(deviceInfo?.locationName);
  const locationName = deviceInfo?.locationName || 'POS Terminal';

  // Lockout countdown timer
  useEffect(() => {
    if (lockoutSeconds > 0) {
      lockoutTimerRef.current = setInterval(() => {
        setLockoutSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(lockoutTimerRef.current);
            setLocalError('');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);
    };
  }, [lockoutSeconds]);

  // Clear errors on new input
  useEffect(() => {
    if (pin.length > 0 && (error || localError)) {
      clearError();
      if (!lockoutSeconds) setLocalError('');
    }
  }, [pin]);

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (pin.length === 4 && !submitting && !lockoutSeconds) {
      handleSubmit();
    }
  }, [pin]);

  const handleSubmit = useCallback(async () => {
    if (pin.length !== 4 || submitting || lockoutSeconds) return;

    setSubmitting(true);
    setLocalError('');
    clearError();

    const result = await pinLogin(pin);
    setSubmitting(false);

    if (!result.success) {
      setPin('');
      setShake(true);
      setTimeout(() => setShake(false), 500);

      if (result.locked) {
        setLockoutSeconds(result.secondsRemaining || 30);
        setLocalError(`Too many attempts. Try again in ${result.secondsRemaining || 30}s`);
      } else {
        setLocalError(result.error || 'Invalid PIN');
      }
    }
  }, [pin, submitting, lockoutSeconds, pinLogin, clearError]);

  const handleDigit = useCallback((digit) => {
    if (pin.length >= 4 || submitting || lockoutSeconds) return;
    setPin((prev) => prev + digit);
  }, [pin.length, submitting, lockoutSeconds]);

  const handleBackspace = useCallback(() => {
    if (submitting) return;
    setPin((prev) => prev.slice(0, -1));
  }, [submitting]);

  const handleClear = useCallback(() => {
    if (submitting) return;
    setPin('');
    setLocalError('');
    clearError();
  }, [submitting, clearError]);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        handleDigit(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'Escape') {
        handleClear();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleDigit, handleBackspace, handleClear]);

  const displayError = localError || error;
  const isLocked = lockoutSeconds > 0;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden select-none"
      style={{ background: 'linear-gradient(145deg, #0d1117 0%, #131d2e 35%, #0f2847 65%, #0d1117 100%)' }}
    >
      {/* Location accent bar at top */}
      <div
        className="absolute top-0 left-0 right-0 h-1.5"
        style={{ background: locationColor }}
      />

      {/* Logo + Location */}
      <div className="text-center mb-6 relative z-10" style={{ animation: 'fadeSlideIn 0.4s ease-out both' }}>
        <img
          src={teletimeLogoWhite}
          alt="Teletime"
          className="w-48 max-w-[70vw] h-auto mx-auto mb-3"
          style={{ filter: 'drop-shadow(0 4px 20px rgba(59, 130, 246, 0.2))' }}
        />
        {/* Location badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mt-2"
          style={{ background: `${locationColor}22`, border: `1px solid ${locationColor}44` }}
        >
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: locationColor }} />
          <span className="text-xs font-medium tracking-wide" style={{ color: locationColor }}>
            {locationName}
          </span>
        </div>
      </div>

      {/* PIN Card */}
      <div
        className={`w-full max-w-xs relative z-10 ${shake ? 'animate-[shake_0.5s_ease-in-out]' : ''}`}
        style={{ animation: shake ? undefined : 'fadeSlideIn 0.4s ease-out 0.1s both' }}
      >
        <div
          className="rounded-2xl shadow-2xl overflow-hidden"
          style={{ background: 'rgba(255, 255, 255, 0.97)', boxShadow: '0 24px 64px rgba(0, 0, 0, 0.35)' }}
        >
          <div className="h-1" style={{ background: locationColor }} />

          <div className="p-6 pt-5">
            <div className="text-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">Enter PIN</h2>
              <p className="text-gray-500 text-xs mt-0.5">4-digit PIN to clock in</p>
            </div>

            {/* Error */}
            {displayError && (
              <div className="mb-4 p-2.5 bg-red-50 border border-red-200 text-red-600 rounded-xl text-xs text-center">
                {displayError}
              </div>
            )}

            {/* Lockout countdown */}
            {isLocked && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl text-sm text-center font-medium">
                Locked — {lockoutSeconds}s remaining
              </div>
            )}

            {/* PIN dots */}
            <div className="flex justify-center gap-4 mb-6">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full transition-all duration-150 ${
                    pin.length > i
                      ? 'scale-110'
                      : 'bg-gray-200'
                  }`}
                  style={pin.length > i ? { background: locationColor } : undefined}
                />
              ))}
            </div>

            {/* Numeric Keypad */}
            <div className="grid grid-cols-3 gap-2.5">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                <button
                  key={digit}
                  type="button"
                  onClick={() => handleDigit(digit)}
                  disabled={isLocked || submitting}
                  className="h-14 rounded-xl text-xl font-semibold text-gray-800 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {digit}
                </button>
              ))}

              {/* Bottom row: Clear, 0, Backspace */}
              <button
                type="button"
                onClick={handleClear}
                disabled={submitting}
                className="h-14 rounded-xl text-xs font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors uppercase tracking-wide"
              >
                Clear
              </button>

              <button
                type="button"
                onClick={() => handleDigit('0')}
                disabled={isLocked || submitting}
                className="h-14 rounded-xl text-xl font-semibold text-gray-800 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                0
              </button>

              <button
                type="button"
                onClick={handleBackspace}
                disabled={submitting}
                className="h-14 rounded-xl text-gray-500 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors flex items-center justify-center"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                  <line x1="18" y1="9" x2="12" y2="15" />
                  <line x1="12" y1="9" x2="18" y2="15" />
                </svg>
              </button>
            </div>

            {/* Submitting indicator */}
            {submitting && (
              <div className="flex items-center justify-center gap-2 mt-4 text-gray-500 text-sm">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verifying...
              </div>
            )}
          </div>
        </div>

        {/* Sign out device link */}
        <button
          type="button"
          onClick={logout}
          className="w-full mt-4 text-gray-500 hover:text-gray-300 text-xs text-center py-2 transition-colors"
        >
          Sign out this terminal
        </button>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
}

export default PinLockScreen;
